import React, { useEffect, useRef, useState } from "react";
import * as rpc from "../../common/jsonRpcRoutes";
import { notifyBugsnag } from "../bugsnag";
import { redirectToOldGuestExperience } from "../utils/redirectToOldExperience";

interface SignatureGuardProps {
  children: React.ReactNode;
  optional?: boolean;
  signature?: string;
  bookingId?: string;
}

const SignatureGuard: React.FC<SignatureGuardProps> = ({
  children,
  optional,
  signature: signatureProp,
  bookingId: bookingIdProp,
}) => {
  const params = new URLSearchParams(window.location.search);
  const signature = signatureProp ?? params.get("signature");
  const bookingId = bookingIdProp ?? params.get("booking_id");
  const [valid, setValid] = useState<null | boolean>(null);
  const hasAttemptedRedirect = useRef(false);

  useEffect(() => {
    // When optional is true, skip validation if both signature and booking_id
    // are absent (e.g. the /book flow which uses pending_booking_id instead)
    if (!signature && !bookingId && optional) {
      setValid(true);
      return;
    }
    if (!signature || !bookingId) {
      setValid(false);
      return;
    }
    rpc
      .call("/validate-signature", { booking_id: bookingId, signature })
      .then(({ valid }) => setValid(valid))
      .catch((_) => setValid(false));
  }, [signature, bookingId, optional]);

  useEffect(() => {
    if (valid !== false || hasAttemptedRedirect.current) return;

    hasAttemptedRedirect.current = true;
    notifyBugsnag(new Error("Invalid signature"), {
      component: "SignatureGuard",
      bookingId,
      timestamp: new Date().toISOString(),
    });
    void redirectToOldGuestExperience().catch((error) => {
      notifyBugsnag(new Error("Failed to redirect to old guest experience"), {
        component: "SignatureGuard",
        bookingId,
        timestamp: new Date().toISOString(),
        error,
      });
    });
  }, [valid, bookingId]);

  if (valid === false) {
    return <div style={{ padding: 32 }}>Redirecting...</div>;
  }
  if (valid === null) {
    return <div style={{ padding: 32 }}>Validating signature...</div>;
  }
  return <>{children}</>;
};

export default SignatureGuard;
